import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageAlert, StandAloneLink } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { ROUTES, StackLink } from '@app/router/constants';
import { useAlertDescription } from '@app/components/Alerts/useAlertDescription';
import AlertSectionLayout from '@app/components/AlertSectionLayout';
import { useNavigate, useResumeServiceAlertIdentification } from '@app/hooks';
import { Alert, AlertAction, AlertType, DisplayLocation, removeAlert, selectAlerts } from '@app/redux/alerts';
import { Issuer, selectConfig } from '@app/redux/config';
import { setCancelOrder } from '@app/redux/disconnect';
import { openInNewTab, postBillingMessage } from '@app/redux/utils';
import { checkAlertDismissed, saveAlertNameInStorage } from '@app/utils';
import { EventTypes } from '@app/types';

import { goToAutopay, goToPayment } from '../navigation';

const AlertsSection = (): JSX.Element => {
  const { t } = useTranslation('Alerts');

  const { alerts, billingAlerts } = useSelector(selectAlerts);
  const [displayAlerts, setDisplayAlerts] = React.useState<Alert[]>([]);

  React.useEffect(() => {
    const allAlerts = [...alerts, ...billingAlerts];

    const alertsToDisplay = allAlerts.filter((alert) => {
      const wasDismissed = checkAlertDismissed(alert.type);
      const shouldDisplay =
        alert.displayLocation === DisplayLocation.GLOBAL ||
        (alert.type !== AlertType.AllDataUsedNextGen && alert.type !== AlertType.AllDataUsed);

      return !wasDismissed && shouldDisplay;
    });

    setDisplayAlerts(alertsToDisplay);
  }, [alerts, billingAlerts, t]);

  return (
    <AlertSectionLayout data-cy="billing-notifications">
      {displayAlerts?.map((alert) => (
        <BillingPageAlert alert={alert} key={alert.type} />
      ))}
    </AlertSectionLayout>
  );
};

const BillingPageAlert = ({ alert }: { alert: Alert }): JSX.Element | null => {
  const dispatch = useDispatch();
  const { issuer } = useSelector(selectConfig);
  const isSalesforce = issuer === Issuer.Salesforce;
  const { goTo } = useNavigate();

  const isResumeServiceAlert = useResumeServiceAlertIdentification(alert);
  const alertDescription = useAlertDescription(alert);

  if (!alert) {
    return null;
  }

  const onEvent = (e: React.MouseEvent, action?: AlertAction | EventTypes) => {
    e.preventDefault();
    if (!action) {
      if (alert.button?.url) {
        if (!alert.button?.openInSameTab) {
          e.preventDefault();
          dispatch(openInNewTab(alert.button.url));
        } else {
          goTo(alert.button.url);
        }
      }
    }

    if (isResumeServiceAlert) {
      dispatch(setCancelOrder());
      // TODO: Remove alert only on mutation success in https://jira.viasat.com/browse/GFBAPPS-581
      saveAlertNameInStorage(AlertType.CancelOrder);
      dispatch(removeAlert(AlertType.CancelOrder));
    }

    if (action === AlertAction.OneTimePayment || action === AlertAction.GoToBilling) {
      goToPayment(dispatch);
    } else if (action === AlertAction.AutoPay) {
      goToAutopay(dispatch);
    } else if (action === AlertAction.ScheduleInstall) {
      goTo(StackLink.ScheduleInstallation);
    } else if (action === AlertAction.RescheduleServiceCall) {
      goTo(StackLink.ScheduleInstallation);
    } else if (action === AlertAction.SignElectronicCustomerAgreement) {
      goTo(ROUTES.SIGN_CONTRACT);
    } else {
      dispatch(postBillingMessage({ eventType: action as EventTypes }));
    }
  };

  return (
    <PageAlert
      data-cy="billing-alert"
      title={alert.title}
      variant={alert.level}
      description={alertDescription}
      preventDefaultOnClose={true}
      onClose={() => {
        saveAlertNameInStorage(alert.type);
        dispatch(removeAlert(alert.type));
      }}
    >
      <>
        {alert.button && (
          <Button
            variant="secondary"
            buttonSize="small"
            mr="16px"
            onClick={(e) => onEvent(e, alert.button?.action)}
            disabled={isSalesforce}
          >
            {alert.button?.label}
          </Button>
        )}
        {alert.link?.url && (
          <StandAloneLink variant="secondary" linkSize="small" href={alert.link.url} disabled={isSalesforce}>
            {alert.link?.label}
          </StandAloneLink>
        )}
      </>
    </PageAlert>
  );
};

export default AlertsSection;
